import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { UserCog } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormDialog } from '@/features/academic/components/FormDialog'
import { ToggleField } from '@/features/academic/components/ToggleField'
import { useAuth } from '@/features/auth/useAuth'
import type { Portal } from '@/features/auth/types'
import { rolesQuery, useSaveUser, type ManagedUser, type UserPayload } from '../api'
import { toUserFormValues, userSchema, type UserFormInput } from '../schemas'

const FIELDS = ['name', 'email', 'role', 'phone', 'password', 'isActive'] as const
const FORM_ID = 'user-form'

const PORTAL_LABELS: Record<Portal, string> = {
  admin: 'Admin portal',
  staff: 'Staff portal',
  family: 'Family portal',
}

interface UserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user?: ManagedUser | null
}

export function UserDialog({ open, onOpenChange, user }: UserDialogProps) {
  const { t } = useTranslation('settings')
  const save = useSaveUser()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<UserCog />}
      title={user ? t('Edit {{name}}', { name: user.name }) : t('New user')}
      description={t('The role decides which portals the person can sign in to and what they can do.')}
      formId={FORM_ID}
      submitLabel={user ? t('Save changes') : t('Create user')}
      pending={save.isPending}
    >
      <UserForm
        user={user ?? null}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, id: user?.id })
          toast.success(user ? t('User updated') : t('User created'), saved.email)
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function UserForm({
  user,
  onSubmit,
}: {
  user: ManagedUser | null
  onSubmit: (values: UserPayload) => Promise<void>
}) {
  const { t } = useTranslation('settings')
  const roles = useQuery(rolesQuery())
  const withGuardians = useGuardians()
  const { user: me } = useAuth()
  const isSelf = user !== null && me?.id === user.id
  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<UserFormInput, unknown, UserPayload>({
    resolver: zodResolver(userSchema(user ? 'edit' : 'create')),
    defaultValues: toUserFormValues(user),
  })
  const role = roles.data?.find((r) => r.value === watch('role'))

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  // The role <select> only applies its default once the options exist.
  if (roles.isPending) return <LoadingState label={t('Loading roles…')} className="min-h-40" />

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
      <FormField label={t('Full name')} error={errors.name?.message} required className="sm:col-span-2">
        <Input autoComplete="off" {...register('name')} />
      </FormField>
      <FormField label={t('Email')} error={errors.email?.message} required hint={t('Used to sign in.')}>
        <Input type="email" autoComplete="off" {...register('email')} />
      </FormField>
      <FormField label={t('Phone')} error={errors.phone?.message}>
        <Input type="tel" autoComplete="off" {...register('phone')} />
      </FormField>
      <FormField
        label={t('Role')}
        error={errors.role?.message}
        required
        className="sm:col-span-2"
        hint={
          role ? (
            <span className="inline-flex flex-wrap items-center gap-1">
              {t('Signs in to')}
              {role.portals.map((p) => (
                <Badge key={p} size="sm">
                  {t(p === 'family' && !withGuardians ? 'Student portal' : PORTAL_LABELS[p])}
                </Badge>
              ))}
            </span>
          ) : undefined
        }
      >
        <Select
          options={(roles.data ?? [])
            // Without guardians, no new parent accounts (an existing one keeps its role).
            .filter((r) => withGuardians || r.value !== 'parent' || user?.role === 'parent')
            .map((r) => ({ value: r.value, label: t(r.label) }))}
          {...register('role')}
        />
      </FormField>
      <FormField
        label={user ? t('New password') : t('Password')}
        error={errors.password?.message}
        required={!user}
        className="sm:col-span-2"
        hint={
          user
            ? t('Leave blank to keep the current password.')
            : t('At least 8 characters. Share it securely; they can change it after signing in.')
        }
      >
        <Input type="password" autoComplete="new-password" {...register('password')} />
      </FormField>
      <Controller
        control={control}
        name="isActive"
        render={({ field }) => (
          <ToggleField
            kind="switch"
            className="rounded-control border border-line bg-surface-muted/60 p-3 sm:col-span-2"
            label={t('Account active')}
            description={
              isSelf
                ? t('You cannot deactivate your own account.')
                : t('Inactive users cannot sign in to any portal.')
            }
            checked={field.value}
            onCheckedChange={field.onChange}
            disabled={isSelf}
          />
        )}
      />
    </form>
  )
}
